<script lang="ts">
  /**
   * Signup: the community link, then an email and a password — and they are in.
   *
   * The link comes first because everything after it can start without them. The moment
   * it is pasted the server resolves the community, checks nobody else has claimed it and
   * starts its keyword audit, so by the time a password is chosen the report is already
   * filling in on the page they land on (Discovery). Joining the community and importing
   * it happen from there, in the background.
   *
   * An invite link (`?invite=`) already names the community, so it starts at the account.
   */
  import { navigate } from '../lib/router.svelte';
  import {
    register,
    startSignup,
    fetchInvitePreview,
    getToken,
    ApiError,
    DISCOVERY_PATH,
    type InvitePreview,
    type SignupStarted,
  } from '../lib/admin';
  import { storedVia } from '../lib/affiliates';
  import CommunityLogo from '../lib/ui/CommunityLogo.svelte';

  if (getToken()) navigate('/admin');

  const params = new URLSearchParams(window.location.search);
  const inviteToken = params.get('invite');

  let url = $state(params.get('url') ?? '');
  let started = $state<SignupStarted | null>(null);
  let email = $state('');
  let password = $state('');
  let busy = $state(false);
  let error = $state<string | null>(null);

  let preview = $state<InvitePreview | null>(null);
  let previewChecked = $state(!inviteToken);
  if (inviteToken) {
    fetchInvitePreview(inviteToken)
      .then((p) => (preview = p))
      .catch(() => (preview = { valid: false }))
      .finally(() => (previewChecked = true));
  }
  const invited = $derived(!!preview?.valid);

  const looksLikeLink = $derived(/skool\.com\/[a-z0-9-]+/i.test(url.trim()));
  const onAccount = $derived(started !== null || invited);

  /** Server details are lower-case fragments ("that community already has…"). */
  function sentence(message: string): string {
    return message.charAt(0).toUpperCase() + message.slice(1) + (/[.!?]$/.test(message) ? '' : '.');
  }

  async function submitLink(e: SubmitEvent | null): Promise<void> {
    e?.preventDefault();
    if (busy || !looksLikeLink) return;
    busy = true;
    error = null;
    try {
      started = await startSignup(url.trim());
    } catch (err: unknown) {
      error =
        err instanceof ApiError && err.status === 429
          ? 'That’s a few tries in a row — give it a minute and try again.'
          : err instanceof ApiError
            ? sentence(err.message)
            : 'We couldn’t read that link. Try again in a moment.';
    } finally {
      busy = false;
    }
  }

  // A pasted link is the whole of this step, so pasting one moves on without a click.
  function onPaste(e: ClipboardEvent): void {
    const pasted = e.clipboardData?.getData('text') ?? '';
    if (/skool\.com\/[a-z0-9-]+/i.test(pasted)) {
      e.preventDefault();
      url = pasted.trim();
      void submitLink(null);
    }
  }

  async function submitAccount(e: SubmitEvent): Promise<void> {
    e.preventDefault();
    if (busy) return;
    busy = true;
    error = null;
    try {
      // A fresh (<60-day) affiliate referral code, if one was captured on the landing
      // page. The server ignores an unknown code, so this never blocks the signup.
      const via = storedVia();
      await register({
        email: email.trim(),
        password,
        ...(started ? { signup: started.signup } : {}),
        ...(invited && inviteToken ? { inviteToken } : {}),
        ...(via ? { via } : {}),
      });
      navigate(DISCOVERY_PATH);
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 410 && started) {
        // The pasted link outlived its window; the account step cannot claim it any more.
        started = null;
      }
      error = err instanceof ApiError ? sentence(err.message) : 'Registration failed.';
    } finally {
      busy = false;
    }
  }
</script>

<div class="console-glow flex min-h-screen items-center justify-center px-6 py-12">
  <div class="w-full max-w-sm">
    <div class="mb-8 text-center">
      <img src="/icon-128.png" alt="" class="mx-auto mb-4 h-11 w-11 rounded-xl" />
      <div class="eyebrow mb-2">Get started</div>
      <h1 class="font-display text-2xl font-bold tracking-tight">
        {onAccount ? 'Create your account' : 'Your Skool community'}
      </h1>
      <p class="mt-2 text-sm text-base-content/72">
        {#if onAccount}
          We’re already auditing your community — pick how you’ll sign in.
        {:else}
          Paste its link. We’ll start checking where it ranks right away.
        {/if}
      </p>
    </div>

    <div class="rounded-box border hair bg-base-200/60 p-6 shadow-2xl shadow-black/40">
      {#if !previewChecked}
        <div class="flex justify-center py-4"><span class="loading loading-spinner loading-md text-primary"></span></div>
      {:else if !onAccount}
        <form class="flex flex-col gap-4" onsubmit={submitLink}>
          <label class="block">
            <span class="eyebrow mb-1.5 block">Community link</span>
            <input
              type="text"
              inputmode="url"
              class="input input-bordered w-full bg-base-100/60 font-mono text-sm"
              bind:value={url}
              onpaste={onPaste}
              placeholder="skool.com/your-community"
              autocomplete="url"
              required
            />
          </label>
          {#if inviteToken}
            <div class="rounded-field border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-warning">
              This invite link is invalid or has expired — you can still sign up with your community link.
            </div>
          {/if}
          {#if error}
            <div class="rounded-field border border-error/30 bg-error/10 px-3 py-2 text-sm text-error">{error}</div>
          {/if}
          <button type="submit" class="btn btn-primary mt-1 gap-2 font-semibold" disabled={busy || !looksLikeLink}>
            {#if busy}<span class="loading loading-spinner loading-sm"></span>{/if}
            Continue
          </button>
        </form>
      {:else}
        <div class="mb-5 flex items-center gap-3 rounded-field border hair bg-base-100/40 px-3 py-2.5">
          {#if started}
            <CommunityLogo name={started.community.display_name} logoUrl={started.community.logo_url} size="h-9 w-9" textClass="text-sm" />
            <div class="min-w-0 flex-1">
              <!-- privacy-ok: the visitor's own community, which they just pasted -->
              <div class="truncate text-sm font-semibold">{started.community.display_name}</div>
              <div class="text-xs text-base-content/60">{started.community.total_members.toLocaleString()} members</div>
            </div>
            <button type="button" class="link link-hover text-xs text-base-content/60" onclick={() => (started = null)}>Change</button>
          {:else}
            <div class="text-sm">
              You’ve been invited to add
              <span class="font-semibold text-base-content">{preview?.community_name || 'your community'}</span>.
            </div>
          {/if}
        </div>
        <form class="flex flex-col gap-4" onsubmit={submitAccount}>
          <label class="block">
            <span class="eyebrow mb-1.5 block">Email</span>
            <input
              type="email"
              class="input input-bordered w-full bg-base-100/60 font-mono text-sm"
              bind:value={email}
              autocomplete="username"
              placeholder="you@example.com"
              required
            />
          </label>
          <label class="block">
            <span class="eyebrow mb-1.5 block">Password</span>
            <input
              type="password"
              class="input input-bordered w-full bg-base-100/60 font-mono text-sm"
              bind:value={password}
              autocomplete="new-password"
              placeholder="At least 8 characters"
              minlength="8"
              required
            />
          </label>
          {#if error}
            <div class="rounded-field border border-error/30 bg-error/10 px-3 py-2 text-sm text-error">{error}</div>
          {/if}
          <button type="submit" class="btn btn-primary mt-1 gap-2 font-semibold" disabled={busy || password.length < 8}>
            {#if busy}<span class="loading loading-spinner loading-sm"></span>{/if}
            Create account
          </button>
          <p class="text-center text-[0.7rem] text-base-content/68">
            Free for communities under 100 members · 30-day free trial above · no card required.
          </p>
        </form>
      {/if}
    </div>

    <p class="mt-5 text-center text-sm text-base-content/72">
      Already have an account?
      <button class="link link-primary link-hover" onclick={() => navigate('/admin/login')}>Sign in</button>
    </p>
  </div>
</div>
